/* eslint-disable react-hooks/set-state-in-effect, prefer-const, react-hooks/exhaustive-deps */
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, ButtonBase, IconButton, Stack, Typography, useMediaQuery } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode, createContext, forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { CalendarEvent } from "../../models";

const today = dayjs();

const Main = styled(Stack)(({ theme }) => ({
  border: "1px solid black",
  borderColor: theme.palette.divider,
  borderRadius: 8,
  "& .day": {
    borderRight: "1px solid black",
    borderBottom: "1px solid black",
    borderColor: theme.palette.divider,
  },
  "& .week:last-child .day": {
    borderBottom: "unset",
  },
  "& .week .day:last-child": {
    borderRight: "unset",
  },
}));

const MobileListContainer = styled(Stack)(({ theme }) => ({
  border: "1px solid black",
  borderColor: theme.palette.divider,
  borderRadius: 8,
  overflowY: "auto",
  flex: 1,
  minHeight: 0,
  position: "relative",
}));

const MobileDayRow = styled(ButtonBase)(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "stretch",
  justifyContent: "flex-start",
  textAlign: "left",
  borderBottom: "1px solid black",
  borderColor: theme.palette.divider,
  padding: theme.spacing(1.25, 1.5),
}));

const LoadMonthButton = styled(ButtonBase)(({ theme }) => ({
  width: "100%",
  justifyContent: "center",
  borderBottom: "1px solid black",
  borderColor: theme.palette.divider,
  padding: theme.spacing(1.2, 1),
  color: theme.palette.primary.main,
  fontWeight: 600,
}));

export function DayHeader(props: { dayNumber?: number; highlighted: boolean }) {
  const { dayNumber, highlighted } = props;
  return (
    <Box
      sx={{
        position: "absolute",
        top: 4,
        right: 4,
        backgroundColor: highlighted ? "primary.main" : null,
        color: highlighted ? "white" : null,
        borderRadius: "50%",
        width: 28,
        height: 28,
        textAlign: "center",
        verticalAlign: "center",
        paddingTop: "4px",
        zIndex: 1,
      }}
    >
      {dayNumber && <Typography variant="body2">{dayNumber}</Typography>}
    </Box>
  );
}

export function Day(props: { date: Dayjs | null }) {
  const { date } = props;
  const calendar = useCalendar();
  const isToday = !!date && date.month() === today.month() && date.date() === today.date() && date.year() === today.year();
  const dayEvents = calendar.events.filter((x) => x.date.year() === date?.year() && x.date.month() === date?.month() && x.date.date() === date?.date());
  const handleClick = () => {
    if (calendar.onDateClick && date) {
      calendar.onDateClick(date);
    }
  };
  return (
    <ButtonBase
      className="day"
      sx={{ flex: 1, position: "relative", padding: 4, p: 1, alignItems: "flex-start", cursor: date === null ? "auto" : "pointer", minHeight: 162 }}
      disableRipple={date === null}
      onClick={handleClick}
    >
      <Stack {...props} gap={0.7} sx={{ flex: 1 }}>
        <DayHeader dayNumber={date?.date()} highlighted={isToday} />
        {!date && <MonthCalendarDayBlock label="" />}
        {dayEvents.map((e) => {
          return e.content;
        })}
      </Stack>
    </ButtonBase>
  );
}

export function Week(props: { index: number }) {
  const { year, month } = useCalendar();
  const weekIndex = props.index;

  const getDate = (dayOfWeek: number) => {
    let firstOfMonth = dayjs(new Date(year, month, 1));
    let date = firstOfMonth.add(weekIndex, "week").day(dayOfWeek);

    // If result is before the 1st, bump forward a week
    if (date.month() !== month) {
      return null;
    }

    return date;
  };

  return (
    <Stack {...props} direction="row" className="week" sx={{ flex: 1 }}>
      {[...Array(7).keys()].map((dayOfWeek) => {
        return <Day key={dayOfWeek} date={getDate(dayOfWeek)}></Day>;
      })}
    </Stack>
  );
}

export function MonthCalendarPicker(props: { month: number; year: number; onChange: (month: number, year: number) => void }): ReactNode {
  const { month, year, onChange } = props;
  const formattedMonth = dayjs(new Date(year, month)).format("MMMM YYYY");

  const handleNext = () => {
    onChange(month === 11 ? 0 : month + 1, month === 11 ? year + 1 : year);
  };

  const handlePrevious = () => {
    onChange(month === 0 ? 11 : month - 1, month === 0 ? year - 1 : year);
  };

  return (
    <Stack direction="row" alignItems="center" gap={2}>
      <IconButton sx={{ width: 40, height: 36 }} onClick={handlePrevious}>
        <FontAwesomeIcon icon={faChevronLeft} />
      </IconButton>
      <Typography variant="h6" sx={{ color: "black", minWidth: 140, textAlign: "center" }}>
        {formattedMonth}
      </Typography>
      <IconButton sx={{ width: 40, height: 36 }} onClick={handleNext}>
        <FontAwesomeIcon icon={faChevronRight} />
      </IconButton>
    </Stack>
  );
}

export function MonthCalendarDayBlock(props: { label: string }) {
  const { label } = props;

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", width: "100%", backgroundColor: "#EEE", position: "absolute", top: 0, bottom: 0, left: 0, right: 0 }}
    >
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}

export const MonthCalendar = forwardRef(function MonthCalendarImpl(
  props: {
    month: number;
    year: number;
    events: CalendarEvent[];
    onDateClick?: (date: Dayjs) => void;
    onMobileRangeChange?: (startMonth: Dayjs, endMonth: Dayjs) => void;
  },
  ref: React.Ref<{ scrollToToday: () => void }>,
) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { month, year, events, onDateClick, onMobileRangeChange } = props;
  const targetDate = useMemo(() => {
    let value = today.startOf("day");
    while ([0, 6].includes(value.day())) {
      value = value.add(1, "day");
    }
    return value;
  }, []);
  const targetMonth = useMemo(() => targetDate.startOf("month"), [targetDate]);
  const [mobileMonths, setMobileMonths] = useState<Dayjs[]>([targetMonth]);
  const mobileContainerRef = useRef<HTMLDivElement | null>(null);
  const todayAnchorRef = useRef<HTMLDivElement | null>(null);
  const pendingScrollBehaviorRef = useRef<ScrollBehavior | null>(null);
  const hasAutoScrolledToTodayRef = useRef(false);
  const [todayAnchorNode, setTodayAnchorNode] = useState<HTMLDivElement | null>(null);

  const getAppBarOffset = useCallback((): number => {
    const appBar = document.querySelector("header.MuiAppBar-root") as HTMLElement | null;
    return appBar?.getBoundingClientRect().height ?? 64;
  }, []);

  // Get the target date for scrolling: if today is a weekend, find the next weekday
  const getScrollTargetDate = useCallback((): Dayjs => {
    let scrollDate = targetDate;
    while ([0, 6].includes(scrollDate.day())) {
      scrollDate = scrollDate.add(1, "day");
    }
    return scrollDate;
  }, [targetDate]);

  const resolveScrollTarget = useCallback((anchor: HTMLDivElement): HTMLElement | null => {
    const preferredContainer = mobileContainerRef.current;
    if (preferredContainer && preferredContainer.scrollHeight > preferredContainer.clientHeight + 1) {
      return preferredContainer;
    }

    let current: HTMLElement | null = anchor.parentElement;
    while (current) {
      const style = window.getComputedStyle(current);
      const canScrollY = /(auto|scroll|overlay)/.test(style.overflowY);
      if (canScrollY && current.scrollHeight > current.clientHeight + 1) {
        return current;
      }
      current = current.parentElement;
    }

    return (document.scrollingElement as HTMLElement | null) ?? null;
  }, []);

  const performScrollToToday = useCallback(
    (behavior: ScrollBehavior): boolean => {
      const todayAnchor = todayAnchorRef.current;
      if (!todayAnchor) return false;

      const scrollTarget = resolveScrollTarget(todayAnchor);
      if (!scrollTarget) return false;

      const anchorRect = todayAnchor.getBoundingClientRect();

      const appBarOffset = getAppBarOffset();

      // If the page itself is scrolling, use window scroll APIs.
      if (scrollTarget === document.scrollingElement) {
        const top = Math.max(0, window.scrollY + anchorRect.top - appBarOffset);
        window.scrollTo({ top, behavior });
        return true;
      }

      const targetRect = scrollTarget.getBoundingClientRect();
      const top = Math.max(0, scrollTarget.scrollTop + (anchorRect.top - targetRect.top) - appBarOffset);

      scrollTarget.scrollTo({ top, behavior });
      return true;
    },
    [resolveScrollTarget, getAppBarOffset],
  );

  const ensureTodayMonthLoaded = useCallback(() => {
    setMobileMonths((currentMonths) => {
      if (currentMonths.some((currentMonth) => currentMonth.isSame(targetMonth, "month"))) {
        return currentMonths;
      }
      return [targetMonth];
    });
  }, [targetMonth]);

  const requestScrollToToday = useCallback(
    (behavior: ScrollBehavior) => {
      const didScroll = performScrollToToday(behavior);
      if (didScroll) return;

      pendingScrollBehaviorRef.current = behavior;
      if (isSmallScreen) {
        ensureTodayMonthLoaded();
      }
    },
    [performScrollToToday, isSmallScreen, ensureTodayMonthLoaded],
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToToday: () => {
        requestScrollToToday("smooth");
      },
    }),
    [requestScrollToToday],
  );

  function calendarRows(year: number, month: number, { startOfWeek = 0, minRows = 0 } = {}) {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0); // last day of month
    const firstWeekday = first.getDay(); // 0=Sun .. 6=Sat
    const firstWeekdayAdj = (firstWeekday - startOfWeek + 7) % 7;
    const totalSlots = firstWeekdayAdj + last.getDate();
    const rows = Math.ceil(totalSlots / 7);
    return Math.max(rows, minRows);
  }

  const context = {
    events,
    month,
    year,
    onDateClick,
  };

  const setTodayRef = useCallback((node: HTMLDivElement | null) => {
    todayAnchorRef.current = node;
    setTodayAnchorNode(node);
  }, []);

  useEffect(() => {
    if (!isSmallScreen) {
      hasAutoScrolledToTodayRef.current = false;
      return;
    }

    setMobileMonths([targetMonth]);
    hasAutoScrolledToTodayRef.current = false;
  }, [isSmallScreen, targetMonth]);

  useEffect(() => {
    if (!isSmallScreen) return;
    if (!onMobileRangeChange || mobileMonths.length === 0) return;
    onMobileRangeChange(mobileMonths[0], mobileMonths[mobileMonths.length - 1]);
  }, [isSmallScreen, mobileMonths, onMobileRangeChange]);

  useEffect(() => {
    if (!isSmallScreen) return;
    if (!pendingScrollBehaviorRef.current) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const behavior = pendingScrollBehaviorRef.current;
        if (!behavior) return;
        const didScroll = performScrollToToday(behavior);
        if (didScroll) {
          pendingScrollBehaviorRef.current = null;
        }
      });
    });
  }, [isSmallScreen, mobileMonths, todayAnchorNode, performScrollToToday]);

  useEffect(() => {
    // Auto-scroll on mount is now handled by CalendarPage with proper timing.
    // This effect only handles retry logic when months are loaded after initial mount.
    if (!isSmallScreen) return;
    if (hasAutoScrolledToTodayRef.current) return;
    if (mobileMonths.length === 0) return;

    // Retry scroll if months were just loaded (and anchor became available)
    // Only retry if we haven't already succeeded
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const didScroll = performScrollToToday("auto");
        if (didScroll) {
          hasAutoScrolledToTodayRef.current = true;
        }
      });
    });
  }, [isSmallScreen, mobileMonths, todayAnchorNode, performScrollToToday]);

  const handleLoadPreviousMonth = () => {
    setMobileMonths((currentMonths) => {
      const firstMonth = currentMonths[0];
      const previousMonth = firstMonth.subtract(1, "month");
      if (currentMonths.some((currentMonth) => currentMonth.isSame(previousMonth, "month"))) {
        return currentMonths;
      }
      return [previousMonth, ...currentMonths];
    });
  };

  const handleLoadNextMonth = () => {
    setMobileMonths((currentMonths) => {
      const lastMonth = currentMonths[currentMonths.length - 1];
      const nextMonth = lastMonth.add(1, "month");
      if (currentMonths.some((currentMonth) => currentMonth.isSame(nextMonth, "month"))) {
        return currentMonths;
      }
      return [...currentMonths, nextMonth];
    });
  };

  return (
    <CalendarContext.Provider value={context}>
      {!isSmallScreen && (
        <Main flex={1} direction="column">
          {[...Array(calendarRows(year, month)).keys()].map((w) => {
            return <Week key={w} index={w}></Week>;
          })}
        </Main>
      )}
      {isSmallScreen && (
        <MobileListContainer ref={mobileContainerRef}>
          <LoadMonthButton onClick={handleLoadPreviousMonth}>
            <Typography variant="body2">Load {mobileMonths[0].subtract(1, "month").format("MMMM YYYY")}</Typography>
          </LoadMonthButton>
          {mobileMonths.map((monthStart, monthIndex) => {
            const daysInMonth = monthStart.daysInMonth();
            const monthLabel = monthStart.format("MMMM YYYY");

            return (
              <Stack key={monthStart.format("YYYY-MM")}>
                <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.100" }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {monthLabel}
                  </Typography>
                </Box>
                {[...Array(daysInMonth).keys()].map((dayIndex) => {
                  const date = monthStart.date(dayIndex + 1);
                  const isWeekend = [0, 6].includes(date.day());
                  const scrollTargetDate = getScrollTargetDate();
                  const isScrollTarget = date.isSame(scrollTargetDate, "day");
                  const isTargetDate = date.isSame(targetDate, "day");
                  const isToday = date.isSame(today, "day");

                  if (isWeekend) {
                    if (isScrollTarget) {
                      return <Box key={date.format("YYYY-MM-DD")} ref={setTodayRef} sx={{ height: 0 }} />;
                    }
                    return null;
                  }

                  const dayEvents = events.filter((event) => event.date.isSame(date, "day"));

                  return (
                    <Box key={date.format("YYYY-MM-DD")}>
                      {isScrollTarget && <Box ref={setTodayRef} sx={{ height: 0 }} />}
                      <MobileDayRow disableRipple={false} onClick={() => onDateClick?.(date)}>
                        <Stack sx={{ width: "100%" }} gap={1}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="body1" fontWeight={isToday ? "bold" : "normal"}>
                              {date.format("dddd, MMMM D")}
                            </Typography>
                            {isToday && (
                              <Typography variant="caption" sx={{ color: "primary.main", fontWeight: "bold" }}>
                                Today
                              </Typography>
                            )}
                          </Stack>
                          <Stack gap={0.7}>
                            {dayEvents.map((event, eventIndex) => {
                              return (
                                <Box key={eventIndex} sx={{ position: "relative", minHeight: 22 }}>
                                  {event.content}
                                </Box>
                              );
                            })}
                          </Stack>
                        </Stack>
                      </MobileDayRow>
                    </Box>
                  );
                })}
              </Stack>
            );
          })}
          <LoadMonthButton onClick={handleLoadNextMonth} sx={{ borderBottom: "none" }}>
            <Typography variant="body2">Load {mobileMonths[mobileMonths.length - 1].add(1, "month").format("MMMM YYYY")}</Typography>
          </LoadMonthButton>
        </MobileListContainer>
      )}
    </CalendarContext.Provider>
  );
});

export interface CalendarState {
  month: number;
  year: number;
  events: CalendarEvent[];
  onDateClick?: (date: Dayjs) => void;
}

const initialState: CalendarState = {
  month: 0,
  year: 2025,
  events: [],
  onDateClick: undefined,
};

export const CalendarContext = createContext(initialState);

export const useCalendar = () => {
  return useContext(CalendarContext);
};
